import type { UploadTask } from '@renderer/types/upload'

/**
 * Retry policy for the renderer upload queue.
 *
 * These deliberately match the main-process photo/strip policy in
 * backgroundJobManager (maxRetries 10, RetryUtil: 7.5s base, capped at 600s).
 * Before this they did not: photos got 10 attempts backing off to 10 minutes
 * while session recordings - the one output a customer cannot retake - got 3
 * attempts backing off to 30s. One flaky hour at a booth therefore uploaded
 * every photo and dropped the video, which is how UD Djaya JAKAL lost the
 * recordings for sessions ifdhwn and 51ik67 on 2026-09-12/13.
 *
 * They live in their own module (rather than in uploadManager) so they can be
 * unit-tested: importing uploadManager instantiates a singleton that touches
 * `window`, and the vitest environment is plain node.
 */
export const MAX_UPLOAD_RETRIES = 10
export const RETRY_BASE_DELAY_MS = 7500
export const RETRY_MAX_DELAY_MS = 600000

/**
 * How many app launches may spend a full retry cycle on a task that already
 * exhausted its retries while online.
 *
 * loadTasks() re-processes any non-completed task whose file is still in
 * IndexedDB, and this fix stops deleting that file - so without a cap a task the
 * server will never accept would re-attempt on every launch for the life of the
 * booth. The file is kept either way; only the automatic re-attempts stop.
 */
export const MAX_LAUNCH_ATTEMPTS = 5

/** Backoff for attempt N (1-based), mirroring RetryUtil.calculateDelay(). */
export function calculateUploadRetryDelay(attempt: number): number {
  return Math.min(RETRY_BASE_DELAY_MS * Math.pow(2, attempt - 1), RETRY_MAX_DELAY_MS)
}

/**
 * Mirrors isTerminalJobError() in src/main/utils/backgroundJobManager.ts; the
 * renderer bundle cannot import from the main bundle. These are the only
 * failures that can never succeed on a later attempt, so retrying them ten
 * times just keeps a dead task alive for the better part of an hour.
 */
const TERMINAL_ERROR_CODES = ['NO_ENTITLEMENT', 'ENTITLEMENT_REQUIRED', 'FORBIDDEN_TERMINAL']

export function isTerminalUploadError(message: string): boolean {
  const haystack = message.toLowerCase()
  if (TERMINAL_ERROR_CODES.some((c) => haystack.includes(c.toLowerCase()))) return true
  // The session id belongs to another account (409 session_id_conflict). The id never
  // frees up, so every retry gets the same refusal.
  if (/\b409\b/.test(message) && haystack.includes('session_id_conflict')) return true
  // Only 402/403 actually mean "not allowed". A 500 quoting the same words back
  // at us is still a server fault and stays retryable.
  return /\b(402|403)\b/.test(message)
}

/**
 * Which task types can fall back to an animated GIF when the video will not
 * upload. 'recording' MUST stay in this list: the booth pages create session
 * recordings with that type (see pages/bahela/Capture.tsx), and gating on
 * 'video' alone left the fallback dead for the only case that has ever needed it.
 */
export function supportsGifFallback(type: UploadTask['type']): boolean {
  return type === 'video' || type === 'recording'
}
